import { _ } from "i18n";
import { CommonModal } from "../../../../../../ui-kit-v2/src/modal/common-modal";
import { RadioGroupField } from "../../../../../../ui-kit-v2/src/radio-button/radio-group";
import "./playerRatingModal.css";
import { useState } from "react";
import {
	type GPGamePlayerResponse,
	PlayerRatingModalData,
	PlayerRatingVisibility,
	RatingPutRequest,
} from "../../../../gamePage/types";
import { MessageCircleMore } from "lucide-react";
import { PlayerRatingTab } from "./PlayerRatingTab";
import { PlayerModalPlayerSelect } from "./PlayerModalPlayerSelect";
import { roundPlayerRatingScore } from "../../../format-player-rating-score";

interface PlayerRatingModalProps {
	modalData: PlayerRatingModalData;
	close: () => void;
	onSubmit?: (id: number, rating: RatingPutRequest) => void;
	showPlayersSelect?: boolean;
	players?: GPGamePlayerResponse[];
	UNSTABLE_portalContainer?: Element;
}

const initModalData: PlayerRatingModalData = {
	id: 0,
	show: false,
	name: null,
	rating: {
		visibility: PlayerRatingVisibility.Coach,
		ratedBy: null,
		ratedAt: null,
	},
};

export function PlayerRatingModal({ modalData, close, onSubmit, showPlayersSelect, players, UNSTABLE_portalContainer }: PlayerRatingModalProps) {
	const [modalState, setModalState] = useState<PlayerRatingModalData>(modalData);
	const [initialRating, setInitialRating] = useState(modalData.rating);

	const isSaveDisabled =
		modalState.id === 0 ||
		(modalState.rating?.score === initialRating?.score && modalState.rating?.visibility === initialRating?.visibility);

	const handlePlayerChange = (playerId: number, playerName: string) => {
		const selectedPlayer = players?.find((player) => player.id === playerId);
		const persistedScore = selectedPlayer?.coachRating?.score ?? null;
		const nextRating = {
			score: persistedScore ?? 0,
			visibility: selectedPlayer?.coachRating?.visibility ?? PlayerRatingVisibility.Coach,
			ratedBy: selectedPlayer?.coachRating?.ratedBy,
			ratedAt: selectedPlayer?.coachRating?.ratedAt,
		};

		setInitialRating({
			...nextRating,
			score: persistedScore,
		});
		setModalState({
			...modalState,
			id: playerId,
			name: selectedPlayer?.name ?? playerName,
			rating: nextRating,
		});
	};

	return (
		<CommonModal
			show={modalData.show}
			title={_("gamePage.playerRating.modal.title")}
			size="md"
			onClose={close}
			UNSTABLE_portalContainer={UNSTABLE_portalContainer}
			primaryButton={{
				text: _("gamePage.playerRating.modal.primaryButton"),
				disabled: isSaveDisabled,
				onClick: () => {
					if (onSubmit) {
						onSubmit(modalState.id, {
							score: roundPlayerRatingScore(modalState.rating?.score ?? 0),
							visibility: modalState.rating?.visibility ?? PlayerRatingVisibility.Coach,
						});

						if (showPlayersSelect) {
							setModalState(initModalData);
						}
						close();
					}
				},
			}}
			secondaryButton={{
				text: _("gamePage.playerRating.modal.secondaryButton"),
				onClick: () => {
					if (showPlayersSelect) {
						setModalState(initModalData);
					}
					close();
				},
			}}
		>
			<div className="playerRatingModal-container">
				{showPlayersSelect && players != null && (
					<PlayerModalPlayerSelect
						label={_("gamePage.playerRating.modal.selectPlayer")}
						placeholder={_("gamePage.playerRatingBadges.modal.selectPlayerPlaceholder")}
						players={players}
						onPlayerChange={handlePlayerChange}
					/>
				)}
				<PlayerRatingTab modalState={modalState} setModalState={setModalState} />
				<div className="playerRatingModal-visibility">
					<div className="visibility-heading">
						<MessageCircleMore size={18} style={{ marginRight: "4px" }} />
						<div>{_("gamePage.playerRating.modal.visibility.title")}</div>
					</div>
					<div className="visibility-text">{_("gamePage.playerRating.modal.visibility.text")}</div>
					<RadioGroupField<PlayerRatingVisibility>
						value={(modalState.rating?.visibility as PlayerRatingVisibility) ?? PlayerRatingVisibility.Coach}
						onChange={(value) => setModalState({ ...modalState, rating: { ...modalState.rating, visibility: value } })}
						options={[
							{ value: PlayerRatingVisibility.Coach, label: _("gamePage.playerRating.modal.visibility.coach") },
							{ value: PlayerRatingVisibility.Player, label: _("gamePage.playerRating.modal.visibility.player") },
							{ value: PlayerRatingVisibility.PlayerParent, label: _("gamePage.playerRating.modal.visibility.playerParent") },
						]}
						orientation="horizontal"
						isRequired
					/>
				</div>
			</div>
		</CommonModal>
	);
}
